import { useEffect, useRef, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { Badge } from '@/components/ui/badge.jsx';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet.jsx';
import { usePeekStore } from '@/stores/peek-store.js';
import { openContactPeek, dealUrl } from '@/lib/links.js';
import { formatCurrency, formatHumanDate } from '@/lib/format.js';
import { api } from '@/lib/api.js';
import useMediaQuery from '@/hooks/useMediaQuery.js';

const SUB_STATUS_VARIANT = {
  pending: 'outline',
  ready: 'info',
  sent: 'info',
  responded: 'success',
  declined: 'muted',
  error: 'accent',
  cancelled: 'muted',
};

export default function CarrierPeekSheet({ id, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const aborted = useRef(false);
  const openPeek = usePeekStore((s) => s.openPeek);
  const isDesktop = useMediaQuery('(min-width: 768px)');

  useEffect(() => {
    if (!id) return;
    aborted.current = false;
    setLoading(true);
    setError(null);
    api
      .get(`/api/tracon/carrier/${encodeURIComponent(id)}/peek`)
      .then((r) => {
        if (!aborted.current) setData(r);
      })
      .catch((err) => {
        if (!aborted.current) setError(err);
      })
      .finally(() => {
        if (!aborted.current) setLoading(false);
      });
    return () => {
      aborted.current = true;
    };
  }, [id]);

  return (
    <Sheet open={!!id} onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        side={isDesktop ? 'right' : 'bottom'}
        className="flex flex-col overflow-hidden p-0 sm:max-w-lg"
      >
        <div className="p-6 pb-0">
          <SheetHeader>
            <SheetTitle>{data?.name || 'Carrier'}</SheetTitle>
            {data?.carrier_slug ? (
              <SheetDescription>
                <span className="font-mono">{data.carrier_slug}</span>
              </SheetDescription>
            ) : null}
          </SheetHeader>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          {loading && !data ? (
            <p className="text-sm text-text-muted">Loading…</p>
          ) : error ? (
            <p className="text-sm text-accent">
              Failed to load carrier: {error.message || 'unknown'}
            </p>
          ) : data ? (
            <>
              {/* On deals */}
              {data.on_deals?.length > 0 ? (
                <div>
                  <h4 className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-text-dim">
                    On deals
                  </h4>
                  <ul className="space-y-1.5">
                    {data.on_deals.map((d) => (
                      <li
                        key={d.deal_id}
                        className="flex items-baseline justify-between gap-2"
                      >
                        <div className="flex items-baseline gap-2 min-w-0">
                          <a
                            href={dealUrl(
                              d.account_id || d.deal_id,
                              d.deal_id
                            )}
                            className="truncate text-sm text-text hover:underline"
                          >
                            {d.account_name || d.app_number || d.deal_id}
                          </a>
                          {d.stage ? (
                            <Badge variant="outline" className="text-[9px]">
                              {d.stage}
                            </Badge>
                          ) : null}
                        </div>
                        {d.premium ? (
                          <span className="shrink-0 font-mono text-xs text-text-muted">
                            {formatCurrency(d.premium)}
                          </span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p className="text-xs italic text-text-dim">
                  Not on any deals yet.
                </p>
              )}

              {/* Submission states */}
              {data.submission_states?.length > 0 ? (
                <div>
                  <h4 className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-text-dim">
                    Submission activity
                  </h4>
                  <ul className="space-y-2">
                    {data.submission_states.map((s, i) => (
                      <li
                        key={s.session_id || i}
                        className="rounded border border-border p-2 text-xs"
                      >
                        <div className="flex items-baseline justify-between gap-2">
                          <Badge
                            variant={
                              SUB_STATUS_VARIANT[s.sub_status] || 'outline'
                            }
                            className="text-[9px]"
                          >
                            {(s.sub_status || 'unknown').toUpperCase()}
                          </Badge>
                          {s.sent_at ? (
                            <span className="text-text-dim">
                              Sent {formatHumanDate(s.sent_at)}
                            </span>
                          ) : null}
                        </div>
                        {s.quotes?.length > 0 ? (
                          <div className="mt-1 text-text-dim">
                            {s.quotes.length} quote
                            {s.quotes.length === 1 ? '' : 's'} received
                          </div>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {/* Reps */}
              <div>
                <h4 className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-text-dim">
                  Underwriter contacts
                </h4>
                {data.reps?.length > 0 ? (
                  <ul className="space-y-1">
                    {data.reps.map((r) => (
                      <li key={r.contact_id}>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            openPeek(openContactPeek(r.contact_id));
                          }}
                          className="text-sm text-text hover:underline"
                        >
                          {r.name || r.contact_id}
                        </button>
                        {r.email ? (
                          <span className="ml-2 text-[10px] text-text-dim">
                            {r.email}
                          </span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <Badge variant="dashed" className="text-[10px]">
                    No underwriter contacts linked yet
                  </Badge>
                )}
              </div>
            </>
          ) : null}
        </div>

        {data?.crm_url ? (
          <div className="border-t border-border bg-surface px-6 py-3 flex justify-end">
            <a
              href={data.crm_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-text-muted hover:text-text"
            >
              <ExternalLink size={12} strokeWidth={1.5} />
              Open in CRM
            </a>
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
